/** Per-user demo visit snapshot. Never part of admin All Visits. */

export const DEMO_SNAPSHOT_STORAGE_PREFIX = "vbot_demo_snapshot";
export const DEMO_SNAPSHOT_LEGACY_KEY = "vbot_demo_snapshot";
export const DEMO_SESSION_ID = "demo-session";

export function resolveAuthUserId(user) {
  if (!user) return null;
  const id = user.userId ?? user.userid ?? user.id;
  if (id == null || id === "") return null;
  return String(id);
}

export function isDemoSession(session) {
  if (!session) return false;
  return (
    session.isDemoEntry === true ||
    String(session.id) === DEMO_SESSION_ID ||
    String(session.sessionId) === DEMO_SESSION_ID
  );
}

export function getDemoSnapshotStorageKey(userId) {
  if (userId == null || userId === "") return null;
  return `${DEMO_SNAPSHOT_STORAGE_PREFIX}:${String(userId)}`;
}

export function shouldShowDemoVisitInSessionList({
  isAdmin = false,
  isDemoMode = false,
  snapshot = null,
} = {}) {
  if (isAdmin) return false;
  return Boolean(isDemoMode || snapshot);
}

export function resolveDemoVisitSelection({
  snapshot,
  currentUserId,
  isAdmin = false,
  isRecording = false,
} = {}) {
  if (isAdmin) return { ok: false, reason: "admin" };
  if (isRecording) return { ok: false, reason: "recording" };
  if (!snapshot) return { ok: false, reason: "missing" };
  if (
    snapshot.ownerUserId &&
    currentUserId &&
    String(snapshot.ownerUserId) !== String(currentUserId)
  ) {
    return { ok: false, reason: "owner-mismatch" };
  }
  return {
    ok: true,
    reason: null,
    activeSession: DEMO_SESSION_ID,
    intendedSessionId: DEMO_SESSION_ID,
    isViewingDemoSnapshot: true,
    isDemoMode: false,
  };
}

export function mergeDemoSessionIntoList(
  userSessions,
  demoSessionEntry,
  { isAdmin = false } = {},
) {
  const sessions = Array.isArray(userSessions)
    ? userSessions.filter((session) => !isDemoSession(session))
    : [];
  if (isAdmin || !demoSessionEntry) return sessions;
  return [
    ...sessions,
    {
      id: DEMO_SESSION_ID,
      sessionId: DEMO_SESSION_ID,
      name: demoSessionEntry.sessionName,
      sessionName: demoSessionEntry.sessionName,
      createdAt: demoSessionEntry.createdAt || null,
      isDemoEntry: true,
      ownerUserId: demoSessionEntry.ownerUserId || null,
      creator: { name: "VirtualScribe" },
    },
  ];
}

function readJson(key) {
  if (!key || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function saveDemoSnapshot(snapshot, userId) {
  const key = getDemoSnapshotStorageKey(userId);
  if (!key) return;
  if (!snapshot) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(
    key,
    JSON.stringify({
      ...snapshot,
      ownerUserId: String(userId),
    }),
  );
}

export function loadDemoSnapshot(userId, { isAdmin = false } = {}) {
  if (isAdmin) return null;
  const id = userId == null || userId === "" ? null : String(userId);
  if (!id) return null;

  const scoped = readJson(getDemoSnapshotStorageKey(id));
  if (scoped) {
    if (scoped.ownerUserId && String(scoped.ownerUserId) !== id) return null;
    return { ...scoped, ownerUserId: scoped.ownerUserId || id };
  }

  const legacy = readJson(DEMO_SNAPSHOT_LEGACY_KEY);
  if (!legacy) return null;
  if (legacy.ownerUserId && String(legacy.ownerUserId) !== id) return null;

  const migrated = { ...legacy, ownerUserId: id };
  saveDemoSnapshot(migrated, id);
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(DEMO_SNAPSHOT_LEGACY_KEY);
  }
  return migrated;
}
